import { useFilterOptions, FilterOptionsStatus } from '../filter-options';
import { type ReactNode, useEffect, useState } from 'react';

import { Button } from '@repo/ui/components/ui/button';
import { Checkbox } from '@repo/ui/components/ui/checkbox';
import { Label } from '@repo/ui/components/ui/label';
import { Input } from '@repo/ui/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@repo/ui/components/ui/sheet';
import { usePhotos } from '@/state/use-photos';
import {
  PHOTO_STATE_LABELS,
  PHOTO_STATES,
  getPhotoVogueService,
  type Photographer,
  type PhotoFilters,
  type PhotoState,
  type PhotoType,
} from '@/services/photovogue';

export interface FilterPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export type PhotoFiltersUpdater =
  | PhotoFilters
  | ((filters: PhotoFilters) => PhotoFilters);

export interface PhotoFilterPanelProps extends FilterPanelProps {
  filters: PhotoFilters;
  updateFilters: (
    filters: PhotoFiltersUpdater,
    debounceSearch?: boolean,
  ) => void | Promise<void>;
  resetFilters: () => void | Promise<void>;
  showStates?: boolean;
  allowedTypes?: PhotoType[];
}

const toggle = <T extends string>(
  arr: T[] | undefined,
  value: T,
): T[] | undefined => {
  const set = new Set<T>(arr ?? []);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  const next = Array.from(set);
  return next.length ? next : undefined;
};

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-cn-muted-foreground">
    {children}
  </p>
);

const selectedValues = (select: HTMLSelectElement): string[] =>
  Array.from(select.selectedOptions, (option) => option.value);

export const PhotoFilterPanel = ({
  open,
  onOpenChange,
  filters,
  updateFilters,
  resetFilters,
  showStates = true,
  allowedTypes = ['best', 'daily'],
}: PhotoFilterPanelProps) => {
  const categories = useFilterOptions(open, 'categories');
  const themes = useFilterOptions(open, 'themes');
  const tags = useFilterOptions(open, 'tags');
  const [photographersLoading, setPhotographersLoading] = useState(false);
  const [photographersError, setPhotographersError] = useState(false);
  const [photographerQuery, setPhotographerQuery] = useState('');
  const [photographerMatches, setPhotographerMatches] = useState<
    Photographer[]
  >([]);

  useEffect(() => {
    if (!open || photographerQuery.trim().length < 2) {
      setPhotographersLoading(false);
      setPhotographersError(false);
      setPhotographerMatches([]);
      return undefined;
    }
    let active = true;
    setPhotographersLoading(true);
    setPhotographersError(false);
    setPhotographerMatches([]);
    const timer = window.setTimeout(() => {
      getPhotoVogueService()
        .getPhotographers({
          filters: { search: photographerQuery.trim() },
          pagination: { page: 0, limit: 8, skip: 0 },
          sort: { field: 'name', direction: 'asc' },
        })
        .then((response) => {
          if (active) setPhotographerMatches(response.data);
        })
        .catch(() => {
          if (active) {
            setPhotographerMatches([]);
            setPhotographersError(true);
          }
        })
        .finally(() => {
          if (active) setPhotographersLoading(false);
        });
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [open, photographerQuery]);

  const toggleState = (value: PhotoState) =>
    updateFilters((f) => ({ ...f, states: toggle(f.states, value) }));
  const setCategory = (value: string) =>
    updateFilters((f) => ({
      ...f,
      category: value || undefined,
    }));
  const setType = (value: PhotoType) =>
    updateFilters((f) => ({
      ...f,
      type: f.type === value ? undefined : value,
    }));
  const setLegacyQueue = (states: PhotoState[], type?: PhotoType) =>
    updateFilters((f) => ({ ...f, states, type }));
  const setUploadedFrom = (value: string) =>
    updateFilters((f) => ({ ...f, uploadedFrom: value || undefined }));
  const setUploadedTo = (value: string) =>
    updateFilters((f) => ({ ...f, uploadedTo: value || undefined }));
  const setSearch = (value: string) =>
    updateFilters((f) => ({ ...f, search: value || undefined }), true);
  const setPhotographer = (photographer: Photographer) => {
    updateFilters((f) => ({ ...f, photographerId: photographer.id }));
    setPhotographerQuery(photographer.name);
    setPhotographerMatches([]);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Filters</SheetTitle>
          <SheetDescription>
            Refine the editorial photo queue using canonical PhotoVogue data.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <section>
            <SectionTitle>Search</SectionTitle>
            <Input
              value={filters.search ?? ''}
              placeholder="Title, description, photographer, or tag"
              onChange={(event) => setSearch(event.target.value)}
            />
          </section>

          <section>
            <SectionTitle>Photographer</SectionTitle>
            <Input
              value={photographerQuery}
              placeholder="Search photographers by name…"
              onChange={(event) => {
                setPhotographerQuery(event.target.value);
                if (filters.photographerId) {
                  updateFilters((f) => ({
                    ...f,
                    photographerId: undefined,
                  }));
                }
              }}
            />
            {photographersLoading && (
              <p role="status">Loading photographers…</p>
            )}
            {photographersError && (
              <p role="alert">
                Could not load photographers. Try searching again.
              </p>
            )}
            {photographerMatches.length > 0 && (
              <div className="mt-1 overflow-hidden rounded-md border bg-white">
                {photographerMatches.map((photographer) => (
                  <button
                    key={photographer.id}
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-zinc-100"
                    onClick={() => setPhotographer(photographer)}
                  >
                    {photographer.name}
                  </button>
                ))}
              </div>
            )}
            {filters.photographerId && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="mt-1"
                onClick={() => {
                  updateFilters((f) => ({
                    ...f,
                    photographerId: undefined,
                  }));
                  setPhotographerQuery('');
                }}
              >
                Clear photographer
              </Button>
            )}
          </section>

          <section>
            <SectionTitle>Upload date</SectionTitle>
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <Label
                  htmlFor="pv-uploaded-from"
                  className="text-xs text-cn-muted-foreground"
                >
                  From
                </Label>
                <input
                  id="pv-uploaded-from"
                  type="date"
                  value={filters.uploadedFrom ?? ''}
                  max={filters.uploadedTo ?? undefined}
                  onChange={(event) => setUploadedFrom(event.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                />
              </div>
              <div className="flex-1 space-y-1">
                <Label
                  htmlFor="pv-uploaded-to"
                  className="text-xs text-cn-muted-foreground"
                >
                  To
                </Label>
                <input
                  id="pv-uploaded-to"
                  type="date"
                  value={filters.uploadedTo ?? ''}
                  min={filters.uploadedFrom ?? undefined}
                  onChange={(event) => setUploadedTo(event.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                />
              </div>
            </div>
          </section>

          {showStates && (
            <>
              <section>
                <SectionTitle>Legacy queues</SectionTitle>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={
                      filters.states?.length === 1 &&
                      filters.states[0] === 'ready' &&
                      !filters.type
                        ? 'default'
                        : 'outline'
                    }
                    onClick={() => setLegacyQueue(['ready'])}
                  >
                    New
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={
                      filters.states?.length === 1 &&
                      filters.states[0] === 'approved' &&
                      !filters.type
                        ? 'default'
                        : 'outline'
                    }
                    onClick={() => setLegacyQueue(['approved'])}
                  >
                    Approved
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={
                      filters.states?.length === 1 &&
                      filters.states[0] === 'approved' &&
                      filters.type === 'best'
                        ? 'default'
                        : 'outline'
                    }
                    onClick={() => setLegacyQueue(['approved'], 'best')}
                  >
                    Best
                  </Button>
                </div>
              </section>

              <section>
                <SectionTitle>State</SectionTitle>
                <div className="space-y-2">
                  {PHOTO_STATES.map((state) => (
                    <label
                      key={state}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={(filters.states ?? []).includes(state)}
                        onCheckedChange={() => toggleState(state)}
                      />
                      {PHOTO_STATE_LABELS[state]}
                    </label>
                  ))}
                </div>
              </section>
            </>
          )}

          <section>
            <SectionTitle>Category</SectionTitle>
            <FilterOptionsStatus label="categories" {...categories} />
            <select
              aria-label="Category"
              aria-busy={categories.loading}
              disabled={categories.loading || categories.error}
              value={filters.category ?? ''}
              onChange={(event) => setCategory(event.target.value)}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="">All categories</option>
              {categories.values.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </section>

          <section>
            <SectionTitle>Editorial type</SectionTitle>
            <div className="flex gap-2">
              {allowedTypes.map((type) => (
                <Button
                  key={type}
                  type="button"
                  size="sm"
                  variant={filters.type === type ? 'default' : 'outline'}
                  onClick={() => setType(type)}
                >
                  {type === 'best' ? 'Best' : 'Pic of the Day'}
                </Button>
              ))}
            </div>
          </section>

          <section>
            <SectionTitle>Themes</SectionTitle>
            <FilterOptionsStatus label="themes" {...themes} />
            <select
              aria-label="Themes"
              aria-busy={themes.loading}
              disabled={themes.loading || themes.error}
              multiple
              value={filters.themes ?? []}
              onChange={(event) =>
                updateFilters((f) => ({
                  ...f,
                  themes: selectedValues(event.target).length
                    ? selectedValues(event.target)
                    : undefined,
                }))
              }
              className="min-h-28 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
            >
              {themes.values.map((theme) => (
                <option key={theme} value={theme}>
                  {theme}
                </option>
              ))}
            </select>
          </section>

          <section>
            <SectionTitle>Tags</SectionTitle>
            <FilterOptionsStatus label="tags" {...tags} />
            <select
              aria-label="Tags"
              aria-busy={tags.loading}
              disabled={tags.loading || tags.error}
              multiple
              value={filters.tags ?? []}
              onChange={(event) =>
                updateFilters((f) => ({
                  ...f,
                  tags: selectedValues(event.target).length
                    ? selectedValues(event.target)
                    : undefined,
                }))
              }
              className="min-h-28 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
            >
              {tags.values.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </section>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                resetFilters();
                setPhotographerQuery('');
              }}
            >
              Reset
            </Button>
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const FilterPanel = ({ open, onOpenChange }: FilterPanelProps) => {
  const { params, updateFilters, resetFilters } = usePhotos();

  return (
    <PhotoFilterPanel
      open={open}
      onOpenChange={onOpenChange}
      filters={params.filters}
      updateFilters={updateFilters}
      resetFilters={resetFilters}
    />
  );
};

export default FilterPanel;
